<template>
  <el-form ref="form" :model="form" :rules="rules" class="form__connect-fb" @submit.native.prevent="onSubmit">
    <!-- PageID -->
    <el-row :gutter="20">
      <el-col :xs="24" :sm="12">
        <el-form-item :label="$t('Page Id')" prop="page_id">
          <el-input :placeholder="`${platform.name} Page-ID`" v-model="form.page_id"></el-input>
        </el-form-item>
      </el-col>
    </el-row>

    <!-- API -->
    <el-row :gutter="20">
      <el-col :xs="24" :sm="12">
        <el-form-item :label="$t('AccessToken')" prop="token">
          <el-input :placeholder="`${platform.name} Access Token`" v-model="form.token" :disabled="true"></el-input>
        </el-form-item>
      </el-col>
      <el-col :xs="24" :sm="12">
        <el-form-item>
          <label>&nbsp;</label>
          <div class="w-100">
            <el-button type="primary" @click="onClickGetAccessToken" :disabled="!form.page_id" :loading="isLoadingAccessToken">
              {{ $t('btn.loginWithFB') }}
            </el-button>

            <el-button type="text" @click="logoutFromFB" :disabled="!form.page_id" class="m-l-2">{{ $t('btn.changeAccount') }}</el-button>
          </div>
        </el-form-item>
      </el-col>
    </el-row>

    <el-row :gutter="20">
      <el-col :xs="24">
        <el-form-item :label="$t('Tags')" prop="tags">
          <el-checkbox-group v-model="form.tags" :min="1">
            <el-checkbox :checked="isTagChecked(tag)" v-for="tag in tags" :value="tag" :label="tag" :key="tag.id">{{
              tag.title
            }}</el-checkbox>
          </el-checkbox-group>
          <p class="m-t-half color-gray">{{ $t('Select at least one tag you want to subscribe to') }}</p>
        </el-form-item>
      </el-col>

      <el-col :xs="24">
        <el-form-item :label="$t('label.autoPublishSocialMedia')" prop="auto_publication">
          <el-switch v-model="form.auto_publication"> </el-switch>
          <p class="m-t-half color-gray">{{ $t('hint.autoPublishSocialMedia') }}</p>
        </el-form-item>
      </el-col>
    </el-row>

    <el-form-item class="m-t-2 m-b-0 text-right">
      <el-button type="primary" native-type="submit" :loading="isLoading">{{ $t('Save') }}</el-button>
    </el-form-item>
  </el-form>
</template>

<script>
/* eslint-disable no-undef */
import { get } from 'lodash';
import { mapGetters } from 'vuex';
import { MODULE_NAME as TAGS_MODULE } from '@/store/modules/bb_tags';
import { MODULE_NAME as USER_MODULE } from '@/store/modules/user';
import * as CompanyService from '@/services/company';
import * as SocialMediaConfigService from '@/services/bb_sm_config';

export default {
  name: 'FormConnectSocialMedia',

  props: {
    data: {
      type: Object,
      required: true
    },

    platform: {
      type: Object,
      required: true,
      validator(item) {
        const VALID_PLATFORMS = ['facebook', 'instagram'];
        return VALID_PLATFORMS.includes(item.slug);
      }
    }
  },

  computed: {
    ...mapGetters({
      tags: [`${TAGS_MODULE}/all`],
      company: `${USER_MODULE}/company`
    }),

    companyId() {
      return get(this.company, 'id');
    },

    isEdit() {
      return get(this.form, 'id') ? true : false;
    },

    transformedTags() {
      let result = [];
      const tags = get(this.form, 'tags', []);

      /**
       * Loop over all tags and build a new object to update the relation
       * See: https://docs.directus.io/api/reference.html#update-item
       */
      if (tags.length) {
        tags.forEach((tag, index) => {
          let obj = null;

          if (this.data.hasOwnProperty('tags') && this.data.tags[index]) {
            // Update relation
            obj = {
              id: this.data.tags[index].id,
              tag: { id: tag.id }
            };
          } else {
            // Create new relation
            obj = {
              tag: { id: tag.id }
            };

            if (this.isEdit) {
              obj.sm_config = { id: this.data.id };
            }
          }

          if (obj) {
            result.push(obj);
          }
        });
      }

      // Delete items
      if (this.data.hasOwnProperty('tags') && result.length < this.data.tags.length) {
        let resultIds = [];
        result.forEach(tag => {
          if (tag.hasOwnProperty('id')) {
            resultIds.push(tag.id);
          }
        });

        // Get tag item that is not in results
        const items = this.data.tags.filter(tagConfig => {
          return !resultIds.includes(tagConfig.id);
        });

        // Create object to delete relation
        if (items.length) {
          items.forEach(item => {
            const obj = {
              id: item.id,
              $delete: true
            };
            result.push(obj);
          });
        }
      }

      return result;
    }
  },

  data() {
    return {
      form: {
        token: '',
        page_id: null,
        auto_publication: true,
        tags: []
      },
      isLoading: false,
      isLoadingAccessToken: false,
      rules: {
        page_id: [
          {
            required: true,
            message: this.$t('Please fill out all required fields'),
            trigger: 'blur'
          }
        ],
        token: [
          {
            required: true,
            message: this.$t('Please fill out all required fields'),
            trigger: 'blur'
          }
        ],
        tags: [
          {
            required: true,
            message: this.$t('Please fill out all required fields'),
            trigger: 'blur'
          }
        ]
      }
    };
  },

  mounted() {
    this.init();
  },

  methods: {
    clear() {
      this.form = {
        page_id: '',
        token: '',
        auto_publication: true,
        tags: []
      };
    },

    async doSave() {
      const ACTION = this.isEdit ? 'updateConfig' : 'saveConfig';
      const PAYLOAD = Object.assign({}, this.form);
      PAYLOAD.company = this.company.id;
      PAYLOAD.name = `${this.company.name} - ${this.platform.name}`;
      PAYLOAD.platform = this.platform.id;

      // Transform tags for request
      PAYLOAD.tags = this.transformedTags;

      try {
        this.isLoading = true;
        let response;
        if (this.isEdit) {
          response = await SocialMediaConfigService[`${ACTION}`](this.form.id, PAYLOAD);
        } else {
          response = await SocialMediaConfigService[`${ACTION}`](PAYLOAD);
        }

        const { data } = response;
        if (data) {
          this.$notify({
            message: this.$t('Your data has successfully been saved'),
            type: 'success',
            showClose: true,
            duration: 8000
          });

          // Trigger refresh to parent
          this.$emit('refresh');
        }
      } catch (error) {
        this.$notify({
          message: this.$t('Error'),
          type: 'error',
          showClose: true,
          duration: 8000
        });
      } finally {
        this.isLoading = false;
      }
    },

    init() {
      this.clear();
      const formDataId = get(this.data, 'id');

      if (formDataId) {
        this.form = Object.assign({}, this.data);
        this.preSelectTags();
      }
    },

    isTagChecked(item) {
      const tags = get(this.form, 'tags', []);
      let found = tags.find(tag => {
        return item.id === tag.id;
      });

      return found ? true : false;
    },

    onClickGetAccessToken() {
      if (!window.FB) {
        return;
      }

      this.$confirm(`Möchten Sie sich mit ${this.platform.name} verbinden? Bitte deaktivieren Sie zuvor Ihren AdBlocker.`, 'Info', {
        confirmButtonText: this.$t('OK'),
        cancelButtonText: this.$t('Cancel'),
        type: 'info'
      })
        .then(() => {
          this.connectWithFB();
        })
        .catch(() => {
          //
        });
    },

    onSubmit() {
      this.$refs['form'].validate(valid => {
        if (valid) {
          this.doSave();
        } else {
          return false;
        }
      });
    },

    preSelectTags() {
      // Clear tags
      this.form.tags = [];

      // Get index of selected tag to be prefilled later
      const tags = get(this.data, 'tags', []);
      if (tags.length) {
        tags.forEach(tagConfig => {
          const index = this.tags.findIndex(item => {
            return item.id === tagConfig.tag.id;
          });

          // Push tags otherwise there are not reactive. Even $set doesn´t work here
          this.form.tags.push(this.tags[index]);
        });
      }
    },

    /**
     * Check if current user is logged in to fb and get long live access token afterwards
     *
     * @return  {void}
     */
    connectWithFB() {
      FB.getLoginStatus(response => {
        console.log('getLoginStatus', response);
        const accessToken = get(response, 'authResponse.accessToken', '');
        if (accessToken) {
          this.getLongLiveAccessTokenFromFB(accessToken);
        } else {
          this.loginWithFB();
        }
      });
    },

    /**
     * Trigger FB Login Dialog and get long live access token later on
     *
     * @return  {void}
     */
    loginWithFB() {
      FB.login(
        response => {
          const accessToken = get(response, 'authResponse.accessToken', '');
          if (accessToken) {
            this.getLongLiveAccessTokenFromFB(accessToken);
          } else {
            this.$notify({
              title: this.$t('Error'),
              message: 'Fehler beim Anmelden bei Facebook',
              type: 'error',
              showClose: true,
              duration: 8000
            });
          }
        },
        { scope: 'pages_manage_posts,pages_show_list,pages_read_engagement,instagram_content_publish,instagram_basic' }
      );
    },

    /**
     * Get long live facebook page access token
     *
     * @param   {String}  userAccessToken
     *
     * @return  {void}
     */
    async getLongLiveAccessTokenFromFB(userAccessToken) {
      try {
        this.isLoadingAccessToken = true;
        const response = await CompanyService.getFBLongLiveAccessToken(this.companyId, userAccessToken);
        const accessToken = get(response, 'data.access_token');
        const errorMessage = get(response, 'data.error.message');
        if (accessToken) {
          // Make the real request with the user long-lived access token
          // so that we get a page access token that not expire
          if (this.platform.slug === 'facebook') {
            this.getPageAccessToken(this.form.page_id, accessToken);
          } else {
            this.form.token = accessToken;
          }
        }
        // Show error message from FB
        if (errorMessage) {
          this.$notify({
            title: this.$t('Error'),
            message: errorMessage,
            type: 'error',
            showClose: true,
            duration: 8000
          });

          // Logout due to wrong session
          FB.logout(() => {
            this.$notify({
              title: this.$t('Error'),
              message: 'Bitte versuchen Sie es erneut.',
              type: 'warning',
              showClose: true,
              duration: 8000
            });
          });
        }
      } catch (error) {
        this.$notify({
          title: this.$t('Error'),
          message: 'Error during request access token',
          type: 'error',
          showClose: true,
          duration: 8000
        });
      } finally {
        this.isLoadingAccessToken = false;
      }
    },

    /**
     * Get page access token from facebook
     *
     * @param   {integer}  pageId           [pageId description]
     * @param   {string}  userAccessToken  [userAccessToken description]
     *
     * @return  {void}                   [return description]
     */
    async getPageAccessToken(pageId, userAccessToken) {
      try {
        this.isLoadingAccessToken = true;
        const response = await CompanyService.getFbPageAccessToken(pageId, userAccessToken);
        console.log('getPageAccessToken', response);
        const accessToken = get(response, 'data.access_token');
        const errorMessage = get(response, 'data.error.message');
        if (accessToken) {
          this.form.token = accessToken;
        }
        // Show error message from FB
        if (errorMessage) {
          this.$notify({
            title: this.$t('Error'),
            message: errorMessage,
            type: 'error',
            showClose: true,
            duration: 8000
          });
        }
      } catch (error) {
        this.$notify({
          title: this.$t('Error'),
          message: 'Error during request access token',
          type: 'error',
          showClose: true,
          duration: 8000
        });
      } finally {
        this.isLoadingAccessToken = false;
      }
    },

    logoutFromFB() {
      FB.logout(() => {
        this.$notify({
          title: this.$t('Success'),
          message: 'Sie wurden von Facebook abgemeldet',
          type: 'info',
          showClose: false,
          duration: 8000
        });
      });
    }
  },

  watch: {
    data() {
      this.init();
    }
  }
};
</script>

<style lang="scss" scoped></style>
